import {
    ButtonGroup,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Drawer,
} from '@cad-arc';
import {
    FormResource,
    TransportForm,
    TransportResource,
    TransportView,
    UnitStateGroupEnumType,
} from '@mark43/cad-api';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { useLocationApi } from '../../location-select/hooks/use-location-api';
import { useDrawerControls } from '../../drawers/components/drawer-context-provider';
import { TransportDrawerForm } from './transport-drawer-form';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import {
    hospitalTransportUnitStateGroups,
    transportDrawerTitlesPerUnitStateGroup,
} from '../constants';

export const TransportDrawer = function TransportDrawer({
    unitId,
    unitStateId,
    agencyEventId,
}: {
    unitId?: number;
    unitStateId?: number;
    agencyEventId?: number;
}): React.ReactElement {
    const { unitStateDataById } = useBootstrapState();
    const drawerControls = useDrawerControls();
    const closeDrawer = useCallback(() => {
        drawerControls.close();
    }, [drawerControls]);
    const form = useForm<TransportForm>({ mode: 'onBlur' });
    const apiClient = useApiClient();
    const { payForEsriLocation } = useLocationApi();
    const [
        showHospitalLocationRequiredErrorMessage,
        setShowHospitalLocationRequiredErrorMessage,
    ] = useState(false);

    const query = useQuery(['transport', unitId], () => {
        if (unitId) {
            return apiClient
                .request<TransportResource.BootstrapTransport>({
                    method: 'POST',
                    url: 'transport/bootstrap/',
                    data: { unitId, unitStateGroup },
                })
                .then((resp) => resp.data.data);
        }
    });

    const { data: transportForm } = query;

    useEffect(() => {
        if (transportForm) {
            form.reset(transportForm, {
                keepDefaultValues: true,
            });
        }
    }, [transportForm, form]);

    const mutation = useMutation(
        ({
            transportView,
            updateUnitLocation,
        }: {
            transportView: TransportView;
            updateUnitLocation: boolean;
        }) => {
            return apiClient.request<FormResource.UpsertTransport>({
                method: 'POST',
                url: 'form/transport/',
                params: {
                    update_unit_location: updateUnitLocation,
                },
                data: transportView,
            });
        },
        {
            onSuccess() {
                closeDrawer();
            },
        }
    );

    const onSubmit = useCallback(
        (data: TransportForm) => {
            if (!unitId || !unitStateId || !agencyEventId) {
                return;
            }
            if (form.getFieldState('consolidatedLocationView').isDirty) {
                const location = form.getValues('consolidatedLocationView');
                if (location !== undefined) {
                    payForEsriLocation(location);
                }
            }

            const selectedHospitalId = form.getValues('hospitalId');
            const unitStateGroup: UnitStateGroupEnumType =
                unitStateDataById[unitStateId]?.unitStateGroup;
            const isHospitalTransportLocationDisplayed = hospitalTransportUnitStateGroups.includes(
                unitStateGroup
            );

            if (isHospitalTransportLocationDisplayed && !selectedHospitalId) {
                setShowHospitalLocationRequiredErrorMessage(true);
                return;
            }

            return mutation.mutate({
                // @ts-expect-error types issue but it works
                transportView: {
                    id: data.id || 0,
                    unitId,
                    unitStateId,
                    agencyEventId,
                    odometerStartDouble: data.odometerStart,
                    odometerEndDouble: data.odometerEnd,
                    location: data.consolidatedLocationView,
                    comments: data.comments,
                    hospitalId: form.getValues('hospitalId'),
                },
                updateUnitLocation: data.updateUnitLocation,
            });
        },
        [agencyEventId, form, mutation, payForEsriLocation, unitStateId, unitId, unitStateDataById]
    );

    if (!unitId || !unitStateId || !agencyEventId) {
        return (
            <DrawerContent>
                <DrawerHeader>Transport</DrawerHeader>
            </DrawerContent>
        );
    }

    const unitStateGroup: UnitStateGroupEnumType = unitStateDataById[unitStateId]?.unitStateGroup;

    return (
        <Drawer variant="overlay" isOpen={true} size="lg" onClose={closeDrawer}>
            <IntegratedFormProvider
                query={query}
                mutation={mutation}
                form={form}
                onSubmit={onSubmit}
            >
                <DrawerContent>
                    <DrawerHeader>
                        {transportDrawerTitlesPerUnitStateGroup[unitStateGroup]}
                    </DrawerHeader>
                    <DrawerBody>
                        <FormBody>
                            <FormProvider {...form}>
                                <TransportDrawerForm
                                    isSubmitInProgress={mutation.isLoading}
                                    showHospitalTransportLocationSelect={hospitalTransportUnitStateGroups.includes(
                                        unitStateGroup
                                    )}
                                    showHospitalLocationRequiredErrorMessage={
                                        showHospitalLocationRequiredErrorMessage
                                    }
                                    setShowHospitalLocationRequiredErrorMessage={
                                        setShowHospitalLocationRequiredErrorMessage
                                    }
                                />
                            </FormProvider>
                        </FormBody>
                    </DrawerBody>
                    <DrawerFooter>
                        <ButtonGroup>
                            <FormCancelButton onClick={closeDrawer} />
                            <FormSaveButton />
                        </ButtonGroup>
                    </DrawerFooter>
                </DrawerContent>
            </IntegratedFormProvider>
        </Drawer>
    );
};

type TransportDrawerType = typeof TransportDrawer;
export type { TransportDrawerType };
